/* eslint-disable react/require-default-props */
/* eslint-disable no-param-reassign */
/* eslint-disable react/destructuring-assignment */
import {
  ActionIcon,
  Badge,
  Button,
  Grid,
  Group,
  Modal,
  Paper,
  Space,
  Switch,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { IconCheck, IconDeviceFloppy, IconNotes, IconPlus } from '@tabler/icons-react';
import { useEffect, useMemo, useRef } from 'react';
import BasicPricing, { BasicPricingData, BasicPricingRef } from '../../../../components/BasicPricing';
import DestinationPicker from '../../../../components/core/DestinationPicker/DestinationPicker';
import PageSection from '../../../../components/core/PageSection/PageSection';
import { UnitOfMeasureType } from '../../../../models/core/cache.type';
import { Action } from '../../../../models/core/core.type';
import { DestinationType } from '../../../../models/core/destinations.type';
import {
  ProposalResiduePlanDestinationType,
  ProposalResiduePlanQuotationDestinationType,
  ProposalResiduePlanQuotationType,
  ProposalResiduePlanType,
  ProposalTipoPrecificacaoEnum,
} from '../../../../models/core/proposals.type';
import { Feature, Financial } from '../../../../utils/constants.utils';
import DeleteConfirmPopover from '../../../../components/DeleteConfirmPopover';
import { buildFakeAuditObject, newGuid } from '../../../../utils/helper.utils';
import ResidueQuotationDestinationForm, {
  ResidueQuotationDestinationFormRef,
} from './ResidueQuotationDestinationForm';

type FormViewData = {
  action: Action;
  id: string;
  idResiduoDestinoFinal: number | null;
  residuoDestinoFinal: string;
  residuoDestinoFinalDescricao: string | null;
  autorizacaoAmbiental: boolean;
  precificacao: BasicPricingData;
  observacao: string | null;
};

type ResiduePlanDestinationModalProps = {
  opened: boolean;
  onClose: () => void;
  modoEdicao: boolean;
  requireSupplier?: boolean;
  referenceData: {
    unitOfMeasures: UnitOfMeasureType[];
  };
  plan?: ProposalResiduePlanType | null;
  origItem: ProposalResiduePlanDestinationType | null;
  item: ProposalResiduePlanDestinationType | null;
  idPropostaResiduoPlano?: number;
  zIndex: number;
  callback?: (
    item: ProposalResiduePlanDestinationType | null,
    action: string,
    confirmed: boolean
  ) =>
    | ProposalResiduePlanDestinationType
    | null
    | void
    | Promise<ProposalResiduePlanDestinationType | null | void>;
  onAddQuotation?: (plan: ProposalResiduePlanType) => void;
  onSetPrimaryQuotation?: (
    plan: ProposalResiduePlanType,
    quotation: ProposalResiduePlanQuotationType
  ) => void;
  onDeleteQuotation?: (plan: ProposalResiduePlanType, quotation: ProposalResiduePlanQuotationType) => void;
  onQuotationSubmit?: (
    quotation: ProposalResiduePlanQuotationType,
    refItem: ProposalResiduePlanDestinationType,
    item: ProposalResiduePlanQuotationDestinationType | null,
    action: string
  ) => void;
};

const buildInitialValues = (
  item: ProposalResiduePlanDestinationType | null,
  origItem: ProposalResiduePlanDestinationType | null
): FormViewData => ({
  action: origItem ? Action.Nothing : Action.Add,
  id: item?.id || item?.idTemporaria || item?.idPropostaResiduoPlanoDestinoFinal?.toString() || newGuid(),
  idResiduoDestinoFinal: item?.idResiduoDestinoFinal || null,
  residuoDestinoFinal: item?.residuoDestinoFinal || '',
  residuoDestinoFinalDescricao: item?.residuoDestinoFinalDescricao || null,
  autorizacaoAmbiental: item?.autorizacaoAmbiental || false,
  precificacao: {
    tipoPrecificacao: item?.precificacao?.tipo || ProposalTipoPrecificacaoEnum.Geral,
    quantidade: item?.precificacao?.quantidade ?? null,
    quantidadeIdUnidadeMedida: item?.precificacao?.quantidadeIdUnidadeMedida?.toString() || null,
    frequenciaIdUnidadeMedida: item?.precificacao?.frequenciaIdUnidadeMedida?.toString() || null,
    preco: item?.precificacao?.preco ?? null,
    imposto: item?.precificacao?.imposto ?? Financial.Tax,
  },
  observacao: item?.observacao || '',
});

export default function ResiduePlanDestinationModal(props: ResiduePlanDestinationModalProps) {
  const refPricing = useRef<BasicPricingRef>(null);
  const quotationFormRefs = useRef<Record<string, ResidueQuotationDestinationFormRef | null>>({});

  const form = useForm<FormViewData>({
    initialValues: buildInitialValues(props.item, props.origItem),
    validate: {
      idResiduoDestinoFinal: (value) => (!value || value <= 0 ? 'Campo obrigatório' : null),
    },
  });

  useEffect(() => {
    form.setValues(buildInitialValues(props.item, props.origItem));
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.item, props.origItem, props.opened]);

  useEffect(() => {
    if (!props.opened) {
      quotationFormRefs.current = {};
    }
  }, [props.opened]);

  let modalActionLabel = 'Adicionar';
  if (props.item) {
    modalActionLabel = props.modoEdicao ? 'Editar' : 'Visualizar';
  }

  const quantityUnitOptions = useMemo(
    () =>
      props.referenceData.unitOfMeasures
        .filter(
          (x) =>
            x.tipo === 'Massa' || x.tipo === 'Volume' || (x.tipo === 'Outro' && x.unidadeMedidaSigla === 'un')
        )
        .map((x) => ({
          value: x.idUnidadeMedida.toString(),
          label: `${x.unidadeMedida} (${x.unidadeMedidaSigla})`,
          group: x.tipo,
        })),
    [props.referenceData.unitOfMeasures]
  );

  const buildDraftRefItem = useMemo<ProposalResiduePlanDestinationType>(() => {
    const precificacaoQuantidadeIdUnidadeMedida = form.values.precificacao.quantidadeIdUnidadeMedida
      ? Number(form.values.precificacao.quantidadeIdUnidadeMedida)
      : null;
    const precificacaoFrequenciaIdUnidadeMedida = form.values.precificacao.frequenciaIdUnidadeMedida
      ? Number(form.values.precificacao.frequenciaIdUnidadeMedida)
      : null;

    return {
      ...buildFakeAuditObject(),
      ...(props.origItem || {}),
      ...form.values,
      id: form.values.id,
      idTemporaria: props.origItem?.idTemporaria || form.values.id,
      idPropostaResiduoPlanoDestinoFinal:
        props.origItem?.idPropostaResiduoPlanoDestinoFinal || props.item?.idPropostaResiduoPlanoDestinoFinal,
      idPropostaResiduoPlano: props.idPropostaResiduoPlano || props.origItem?.idPropostaResiduoPlano || -1,
      precificacao: {
        tipo: form.values.precificacao.tipoPrecificacao,
        quantidade: form.values.precificacao.quantidade,
        quantidadeIdUnidadeMedida: precificacaoQuantidadeIdUnidadeMedida,
        quantidadeUnidadeMedida:
          props.referenceData.unitOfMeasures.find(
            (x) => x.idUnidadeMedida === precificacaoQuantidadeIdUnidadeMedida
          )?.unidadeMedida || null,
        quantidadeUnidadeMedidaSigla:
          props.referenceData.unitOfMeasures.find(
            (x) => x.idUnidadeMedida === precificacaoQuantidadeIdUnidadeMedida
          )?.unidadeMedidaSigla || null,
        frequenciaIdUnidadeMedida: precificacaoFrequenciaIdUnidadeMedida,
        frequenciaUnidadeMedida:
          props.referenceData.unitOfMeasures.find(
            (x) => x.idUnidadeMedida === precificacaoFrequenciaIdUnidadeMedida
          )?.unidadeMedida || null,
        frequenciaUnidadeMedidaSigla:
          props.referenceData.unitOfMeasures.find(
            (x) => x.idUnidadeMedida === precificacaoFrequenciaIdUnidadeMedida
          )?.unidadeMedidaSigla || null,
        preco: form.values.precificacao.preco,
        imposto: form.values.precificacao.imposto,
      },
    } as ProposalResiduePlanDestinationType;
  }, [
    form.values,
    props.idPropostaResiduoPlano,
    props.item,
    props.origItem,
    props.referenceData.unitOfMeasures,
  ]);

  const visibleDestinationQuotations = (props.plan?.cotacoes || []).filter(
    (quotation) => (quotation.action || Action.Nothing) !== Action.Delete
  );

  const canAddQuotation = useMemo(() => {
    const hasDestination = !!form.values.idResiduoDestinoFinal;
    const hasRequiredPricing =
      form.values.precificacao.tipoPrecificacao !== ProposalTipoPrecificacaoEnum.Geral ||
      (form.values.precificacao.quantidade !== null &&
        !Number.isNaN(Number(form.values.precificacao.quantidade)) &&
        !!form.values.precificacao.quantidadeIdUnidadeMedida &&
        form.values.precificacao.preco !== null &&
        !Number.isNaN(Number(form.values.precificacao.preco)) &&
        form.values.precificacao.imposto !== null &&
        !Number.isNaN(Number(form.values.precificacao.imposto)));

    return hasDestination && hasRequiredPricing;
  }, [form.values]);

  const matchesQuotationItem = (
    quotationItem: ProposalResiduePlanQuotationDestinationType,
    refItem: ProposalResiduePlanDestinationType
  ) => {
    const refKeys = [refItem.id, refItem.idTemporaria, refItem.idPropostaResiduoPlanoDestinoFinal].filter(
      (value) => value !== null && value !== undefined && value !== ''
    );
    const quotationKeys = [
      quotationItem.id,
      quotationItem.idTemporaria,
      quotationItem.idPropostaResiduoPlanoDestinoFinal,
    ].filter((value) => value !== null && value !== undefined && value !== '');

    if (refKeys.length && quotationKeys.length) {
      return refKeys.some((refKey) =>
        quotationKeys.some((quotationKey) => `${refKey}` === `${quotationKey}`)
      );
    }

    return quotationItem.idResiduoDestinoFinal === refItem.idResiduoDestinoFinal;
  };

  const getQuotationKey = (quotation: ProposalResiduePlanQuotationType) =>
    quotation.id || quotation.idPropostaResiduoPlanoCotacao?.toString() || quotation.idTemporaria || '';

  const synchronizeGeneralPricingToQuotations = (
    refItem: ProposalResiduePlanDestinationType | null | undefined
  ) => {
    if (
      !refItem ||
      refItem.precificacao?.tipo !== ProposalTipoPrecificacaoEnum.Geral ||
      !props.plan ||
      !props.onQuotationSubmit
    ) {
      return;
    }

    visibleDestinationQuotations.forEach((quotation) => {
      const quotationItem =
        quotation.destinoFinal && matchesQuotationItem(quotation.destinoFinal, refItem)
          ? quotation.destinoFinal
          : null;

      if (!quotationItem) {
        return;
      }

      props.onQuotationSubmit?.(
        quotation,
        refItem,
        {
          ...quotationItem,
          action: quotationItem.idPropostaResiduoPlanoCotacaoDestinoFinal
            ? Action.Modify
            : quotationItem.action || Action.Nothing,
          precificacao: { ...refItem.precificacao },
        },
        quotationItem.idPropostaResiduoPlanoCotacaoDestinoFinal ? Action.Modify : Action.Nothing
      );
    });
  };

  const isModified = (pricingData?: BasicPricingData | null) => {
    const origData = `${props.origItem?.idResiduoDestinoFinal || ''}|${
      props.origItem?.autorizacaoAmbiental || ''
    }|${props.origItem?.precificacao?.tipo || ''}|${Number(props.origItem?.precificacao?.quantidade) || ''}|${
      props.origItem?.precificacao?.quantidadeIdUnidadeMedida || ''
    }|${props.origItem?.precificacao?.frequenciaIdUnidadeMedida || ''}|${
      Number(props.origItem?.precificacao?.preco) || ''
    }|${Number(props.origItem?.precificacao?.imposto) || ''}|${props.origItem?.observacao || ''}`;
    const formData = `${form.values.idResiduoDestinoFinal || ''}|${form.values.autorizacaoAmbiental || ''}|${
      pricingData?.tipoPrecificacao || ''
    }|${Number(pricingData?.quantidade) || ''}|${pricingData?.quantidadeIdUnidadeMedida || ''}|${
      pricingData?.frequenciaIdUnidadeMedida || ''
    }|${Number(pricingData?.preco) || ''}|${Number(pricingData?.imposto) || ''}|${
      form.values.observacao || ''
    }`;
    return origData !== formData;
  };

  const savePlanItem = async (
    action = 'callback',
    closeOnSuccess = false
  ): Promise<ProposalResiduePlanDestinationType | null> => {
    if (!props.modoEdicao) {
      if (closeOnSuccess) {
        props.onClose();
      }
      return null;
    }

    const pricingData = refPricing.current?.validate();
    if (form.validate().hasErrors || !pricingData) {
      return null;
    }

    let nextAction = form.values.action;
    if (props.origItem && isModified(pricingData) && props.origItem.idPropostaResiduoPlanoDestinoFinal) {
      nextAction = Action.Modify;
    } else if (props.origItem) {
      nextAction = props.item?.action || Action.Nothing;
    }

    const formItem: ProposalResiduePlanDestinationType = {
      ...buildFakeAuditObject(),
      ...(props.origItem || {}),
      ...form.values,
      action: nextAction,
      id: form.values.id,
      idTemporaria: props.origItem?.idTemporaria || form.values.id,
      idPropostaResiduoPlano: props.idPropostaResiduoPlano || props.origItem?.idPropostaResiduoPlano || -1,
      precificacao: {
        tipo: pricingData.tipoPrecificacao,
        quantidade: pricingData.quantidade,
        quantidadeIdUnidadeMedida: pricingData.quantidadeIdUnidadeMedida
          ? Number(pricingData.quantidadeIdUnidadeMedida)
          : null,
        quantidadeUnidadeMedida: null,
        quantidadeUnidadeMedidaSigla: null,
        frequenciaIdUnidadeMedida: pricingData.frequenciaIdUnidadeMedida
          ? Number(pricingData.frequenciaIdUnidadeMedida)
          : null,
        frequenciaUnidadeMedida: null,
        frequenciaUnidadeMedidaSigla: null,
        preco: pricingData.preco,
        imposto: pricingData.imposto,
      },
    };

    formItem.precificacao.quantidadeUnidadeMedida =
      props.referenceData.unitOfMeasures.find(
        (x) => x.idUnidadeMedida === formItem.precificacao.quantidadeIdUnidadeMedida
      )?.unidadeMedida || null;
    formItem.precificacao.quantidadeUnidadeMedidaSigla =
      props.referenceData.unitOfMeasures.find(
        (x) => x.idUnidadeMedida === formItem.precificacao.quantidadeIdUnidadeMedida
      )?.unidadeMedidaSigla || null;
    formItem.precificacao.frequenciaUnidadeMedida =
      props.referenceData.unitOfMeasures.find(
        (x) => x.idUnidadeMedida === formItem.precificacao.frequenciaIdUnidadeMedida
      )?.unidadeMedida || null;
    formItem.precificacao.frequenciaUnidadeMedidaSigla =
      props.referenceData.unitOfMeasures.find(
        (x) => x.idUnidadeMedida === formItem.precificacao.frequenciaIdUnidadeMedida
      )?.unidadeMedidaSigla || null;
    formItem.observacao = formItem.observacao?.trim() || null;

    const callbackResult = await props.callback?.(formItem, action, true);
    const savedItem = callbackResult || formItem;

    synchronizeGeneralPricingToQuotations(savedItem);

    if (closeOnSuccess) {
      props.onClose();
    }

    return savedItem;
  };

  const handleSubmit = async (action = 'callback') => {
    if (!props.modoEdicao) {
      props.onClose();
      return;
    }

    const savedPlanItem = await savePlanItem(action, false);
    if (!savedPlanItem) {
      return;
    }

    const submitResults = await visibleDestinationQuotations.reduce<Promise<(boolean | undefined)[]>>(
      async (promiseAcc, quotation) => {
        const acc = await promiseAcc;
        const result = await quotationFormRefs.current[getQuotationKey(quotation)]?.submit('autosave');
        acc.push(result);
        return acc;
      },
      Promise.resolve([])
    );

    if (submitResults.some((result) => result === false)) {
      return;
    }

    props.onClose();
  };

  return (
    <Modal
      opened={props.opened}
      onClose={props.onClose}
      title={`Plano/Destino final - ${modalActionLabel}`}
      size="75%"
      zIndex={props.zIndex}
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <Paper shadow="xs" p="md" withBorder>
        <PageSection
          size="lg"
          color={Feature.Home.Proposal.color}
          label="Plano de Destino Final"
          text="Defina o destino final, a necessidade de autorização ambiental e as observações deste item."
        />
        <Space h="xs" />
        <Grid columns={6}>
          <Grid.Col span={4}>
            <DestinationPicker
              label="Destino Final"
              formItem={form.getInputProps('idResiduoDestinoFinal')}
              idsToBeDisabled={[]}
              callback={(item: DestinationType | null) => {
                if (item) {
                  form.setFieldValue('idResiduoDestinoFinal', item.idResiduoDestinoFinal);
                  form.setFieldValue('residuoDestinoFinal', item.residuoDestinoFinal);
                  form.setFieldValue('residuoDestinoFinalDescricao', item.descricao);
                }
              }}
              disabled={!props.modoEdicao}
              zIndex={props.zIndex + 1}
              useCache
            />
          </Grid.Col>
          <Grid.Col span={2}>
            <div style={{ marginTop: 29 }}>
              <Switch
                label="Autorização Ambiental?"
                disabled={!props.modoEdicao}
                {...form.getInputProps('autorizacaoAmbiental', { type: 'checkbox' })}
              />
            </div>
          </Grid.Col>
        </Grid>
        <Space h="xs" />
        <Textarea
          leftSection={<IconNotes size={15} />}
          label="Observação"
          placeholder="Observação sobre o destino final"
          autosize
          maxLength={500}
          disabled={!props.modoEdicao}
          {...form.getInputProps('observacao')}
        />
      </Paper>
      <Space h="xs" />
      <Paper shadow="xs" p="md" withBorder>
        <PageSection
          size="lg"
          color={Feature.Home.Proposal.color}
          label="Precificação do Cliente"
          text="Representa os valores que serão cobrados do cliente para este item do plano."
        />
        <Space h="xs" />
        <BasicPricing
          ref={refPricing}
          isEdit={props.modoEdicao}
          disabled={!props.modoEdicao}
          zIndex={props.zIndex + 1}
          quantityMin={0}
          quantityDefaultValue={0}
          withFrequency={false}
          value={form.values.precificacao}
          quantityUnitOptions={quantityUnitOptions}
          onChange={(pricingData) => {
            form.setFieldValue('precificacao', pricingData);
          }}
        />
      </Paper>
      <Space h="xs" />
      <Paper shadow="xs" p="md" withBorder>
        <Group justify="space-between">
          <PageSection
            size="lg"
            color={Feature.Home.Proposal.color}
            label="Cotações"
            text="Cadastre e revise as cotações deste item do plano."
          />
          {props.modoEdicao && props.plan && props.onAddQuotation && (
            <Button
              type="button"
              size="xs"
              leftSection={<IconPlus size={16} />}
              disabled={!canAddQuotation}
              onClick={() => {
                props.onAddQuotation?.(props.plan!);
              }}
            >
              Adicionar cotação
            </Button>
          )}
        </Group>
        <Space h="xs" />

        {!buildDraftRefItem.idResiduoDestinoFinal && (
          <Text size="sm" c="dimmed">
            Selecione o destino final do item do plano para cadastrar fornecedores.
          </Text>
        )}

        {!!buildDraftRefItem.idResiduoDestinoFinal && !visibleDestinationQuotations.length && (
          <Text size="sm" c="dimmed">
            Nenhuma cotação disponível para este plano.
          </Text>
        )}

        {!!buildDraftRefItem.idResiduoDestinoFinal &&
          visibleDestinationQuotations.map((quotation) => {
            const quotationKey = getQuotationKey(quotation);
            const quotationItem =
              (quotation.destinoFinal && (quotation.destinoFinal.action || Action.Nothing) !== Action.Delete
                ? quotation.destinoFinal
                : null) && matchesQuotationItem(quotation.destinoFinal!, buildDraftRefItem)
                ? quotation.destinoFinal
                : null;

            return (
              <Paper key={quotationKey} shadow="xs" p="md" withBorder mt="sm">
                <Group justify="space-between">
                  <Group gap="xs">
                    <Text fw={500}>Cotação #{quotation.idPropostaResiduoPlanoCotacao || '-'}</Text>
                    {quotation.primario && (
                      <Badge color="green" variant="light">
                        Primária
                      </Badge>
                    )}
                  </Group>

                  <Group gap="xs">
                    {props.modoEdicao && props.plan && props.onSetPrimaryQuotation && !quotation.primario && (
                      <Tooltip label="Definir como primária" withArrow>
                        <ActionIcon
                          size="sm"
                          color="green"
                          variant="light"
                          disabled={!quotation.completo || !quotationItem}
                          onClick={() => props.onSetPrimaryQuotation?.(props.plan!, quotation)}
                        >
                          <IconCheck size={15} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    {props.modoEdicao && props.plan && props.onDeleteQuotation && (
                      <DeleteConfirmPopover
                        label="Excluir cotação"
                        message="Esta ação removerá a cotação do item do plano."
                        zIndex={props.zIndex + 1}
                        onConfirm={() => props.onDeleteQuotation?.(props.plan!, quotation)}
                      />
                    )}
                  </Group>
                </Group>

                {props.onQuotationSubmit && (
                  <>
                    <Space h="xs" />
                    <ResidueQuotationDestinationForm
                      ref={(instance) => {
                        quotationFormRefs.current[quotationKey] = instance;
                      }}
                      modoEdicao={props.modoEdicao}
                      requireSupplier={props.requireSupplier}
                      referenceData={props.referenceData}
                      origItem={quotationItem}
                      item={quotationItem}
                      refItem={buildDraftRefItem}
                      idPropostaResiduoPlanoCotacao={quotation.idPropostaResiduoPlanoCotacao}
                      zIndex={props.zIndex + 1}
                      hideCustomerSection={
                        buildDraftRefItem.precificacao?.tipo === ProposalTipoPrecificacaoEnum.Geral
                      }
                      submitLabel="Salvar cotação"
                      callback={async (item, refItem, action, confirmed) => {
                        if (!confirmed || !item || !refItem) {
                          return;
                        }

                        const savedRefItem = await savePlanItem('autosave', false);
                        if (!savedRefItem) {
                          return;
                        }

                        props.onQuotationSubmit?.(quotation, savedRefItem, item, action);
                        showNotification({
                          title: 'Plano/Destino final - Cotação',
                          message: 'Cotação salva com sucesso.',
                          color: 'green',
                        });
                      }}
                    />
                  </>
                )}
              </Paper>
            );
          })}
      </Paper>
      <Space h="xl" />
      <Group justify="right">
        {props.modoEdicao ? (
          <Button type="button" leftSection={<IconDeviceFloppy size={18} />} onClick={() => handleSubmit()}>
            Salvar
          </Button>
        ) : (
          <Button type="button" onClick={props.onClose}>
            Fechar
          </Button>
        )}
      </Group>
    </Modal>
  );
}
